import React, {useCallback} from 'react';
import {Checkbox, FormControlLabel} from '@mui/material';
import {CheckboxCheckedIcon, CheckboxUncheckedIcon} from 'components/icons';
import type {BaseFieldProps} from './types';
import {Layout} from '../layout';
import './CheckboxField.scss';
import {FieldLabel} from './FieldLabel';

type Props = Omit<BaseFieldProps, 'label'> & {
  /** Label displayed above field */
  label?: string;
  /** Label displayed next to check box */
  boxLabel?: string;
  /** Unique id for field */
  fieldId: string;
  name: string;
  value?: boolean | null;
  /**
   * Font variant to use for option labels, default label
   */
  variant?: 'button';
  onChange?: (value: boolean) => void;
};

const checkboxSx = {padding: 0.75} as const;

export const CheckboxField: React.FC<Props> = ({
  label,
  boxLabel,
  fieldId,
  name,
  value,
  readonly = false,
  onChange,
  layout,
}) => {
  const handleToggleValue = useCallback(() => {
    onChange && onChange(!value);
  }, [onChange, value]);

  return (
    <Layout adjustLeft {...layout}>
      {label && <FieldLabel label={label} htmlFor={fieldId} />}
      {boxLabel ? (
        <FormControlLabel
          label={boxLabel}
          htmlFor={fieldId}
          control={
            <Checkbox
              id={fieldId}
              name={name}
              checked={!!value}
              readOnly={readonly}
              disabled={readonly}
              onClick={handleToggleValue}
              icon={<CheckboxUncheckedIcon />}
              checkedIcon={<CheckboxCheckedIcon />}
              sx={checkboxSx}
            />
          }
        />
      ) : (
        <Checkbox
          id={fieldId}
          name={name}
          checked={!!value}
          readOnly={readonly}
          disabled={readonly}
          onClick={handleToggleValue}
          icon={<CheckboxUncheckedIcon />}
          checkedIcon={<CheckboxCheckedIcon />}
          sx={checkboxSx}
        />
      )}
    </Layout>
  );
};
